import {
  useEffect,
  useMemo,
  useRef,
  type Dispatch,
  type SetStateAction,
} from 'react';
import {
  AddOutlined,
  ArrowBackOutlined,
  ArrowDownwardOutlined,
  ArrowUpwardOutlined,
  DeleteOutlined,
  ExpandMoreOutlined,
  PublishOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import type {
  WorkflowAccessRule,
  WorkflowApprovalStepDefinition,
  WorkflowTemplate,
  WorkflowTemplateCreateInput,
  WorkflowTemplateIconKey,
} from '../model';
import {
  ChoiceControl,
  ChoiceGroup,
  createFocusRingStyles,
  designTokens,
  EditPage,
  FormTextField,
  SectionCard,
  ValueBadge,
} from '../../../design-system';
import { WorkflowFieldsEditor } from '../WorkflowFieldsEditor';
import type {
  WorkflowApprovalLabel,
  WorkflowApprovalRoleOption,
  WorkflowApprovalTemplate,
} from '../approval-settings/public';
import {
  WorkflowPersonPicker,
  WorkflowRolePicker,
  WorkflowOrganizationPicker,
  type WorkflowOrganizationOption,
  type WorkflowPersonOption,
} from '../WorkflowReferencePickers';
import {
  workflowTemplateIconOptions,
} from '../workflowTemplateIcons';
import {
  groupWorkflowApprovalSteps,
  insertWorkflowApprovalStepInStage,
  mergeWorkflowApprovalStageWithPrevious,
  moveWorkflowApprovalStage,
  splitWorkflowApprovalStageAt,
} from '../workflowApprovalStages';
import { WorkflowTemplatePreview } from './WorkflowTemplatePreview';
import { WorkflowTemplateLayout } from './WorkflowTemplateLayout';
import { WorkflowDurationField } from './WorkflowDurationField';
import {
  newAccessRule,
  newApprovalStep,
  reorderSteps,
  templateValidationMessage,
  updateAccessRule,
} from '../models/workflowTemplatePageModel';

function WorkflowTemplateIconPicker({ value, onChange }: { value: WorkflowTemplateIconKey; onChange: (value: WorkflowTemplateIconKey) => void }) {
  return <Box sx={{ gridColumn: '1 / -1', minWidth: 0 }}>
    <Typography id="workflow-template-icon-label" variant="body2" sx={{ mb: 0.75, fontWeight: 700 }}>아이콘</Typography>
    <ChoiceGroup
      aria-labelledby="workflow-template-icon-label"
      name="workflow-template-icon"
      value={value}
      onValueChange={(nextValue) => onChange(nextValue as WorkflowTemplateIconKey)}
      sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(3, minmax(0, 1fr))', sm: 'repeat(5, minmax(0, 1fr))', md: 'repeat(9, minmax(0, 1fr))' }, gap: 0.75 }}
    >
      {workflowTemplateIconOptions.map((option) => {
        const selected = option.key === value;
        return <ChoiceControl
          key={option.key}
          value={option.key}
          kind="radio"
          size="small"
          controlSx={{ position: 'absolute', width: '1px', height: '1px', p: 0, opacity: 0, pointerEvents: 'none' }}
          label={<Stack spacing={0.45} sx={{ width: '100%', alignItems: 'center' }}>
            <Box aria-hidden sx={{ width: 34, height: 34, display: 'grid', placeItems: 'center', borderRadius: designTokens.radius.surface, bgcolor: option.surface, color: option.color, '& svg': { fontSize: 19 } }}>{option.icon}</Box>
            <Typography variant="caption" sx={{ color: selected ? designTokens.color.actionDark : designTokens.color.secondaryText, fontWeight: selected ? 750 : 600 }}>{option.label}</Typography>
          </Stack>}
          sx={{
            position: 'relative', minWidth: 0, minHeight: 68, m: 0, px: 0.5, py: 0.75,
            border: 1, borderColor: selected ? designTokens.color.brandBorder : designTokens.color.divider,
            borderRadius: designTokens.radius.surface, bgcolor: selected ? designTokens.color.actionSoft : designTokens.color.canvas,
            transition: 'border-color 140ms ease, background-color 140ms ease', cursor: 'pointer',
            '&:hover': { bgcolor: selected ? designTokens.color.actionSoft : designTokens.color.sidebarSurface },
            '&:has(.Mui-focusVisible)': createFocusRingStyles(),
            '& .MuiFormControlLabel-label': { width: '100%', minWidth: 0 },
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        />;
      })}
    </ChoiceGroup>
  </Box>;
}

type TemplateEditorPageProps = {
  value: WorkflowTemplateCreateInput;
  editing?: WorkflowTemplate;
  approvalLabels: Array<{ id?: string; name: string; tone: WorkflowApprovalLabel['tone'] }>;
  approvalTemplates: WorkflowApprovalTemplate[];
  personOptions: readonly WorkflowPersonOption[];
  organizationOptions: readonly WorkflowOrganizationOption[];
  personOptionsLoading: boolean;
  personOptionsError?: string;
  onRetryPersonOptions: () => void;
  roleOptions: readonly WorkflowApprovalRoleOption[];
  loadRoleOptions: (query: string) => Promise<WorkflowApprovalRoleOption[]>;
  loadAccessRoleOptions: (query: string) => Promise<WorkflowApprovalRoleOption[]>;
  roleOptionsLoading: boolean;
  roleOptionsError?: string;
  onRetryRoleOptions: () => void;
  onChange: Dispatch<SetStateAction<WorkflowTemplateCreateInput>>;
  onClose: () => void;
  onSubmit: (publish: boolean) => void;
  saving: boolean;
  error: string;
  notice: string;
  editConflict: boolean;
  recoveringConflict: boolean;
  onReloadLatest: () => void;
  canPublish: boolean;
};

export function TemplateEditorPage({
  value,
  editing,
  approvalLabels,
  approvalTemplates,
  personOptions,
  organizationOptions,
  personOptionsLoading,
  personOptionsError,
  onRetryPersonOptions,
  roleOptions,
  loadRoleOptions,
  loadAccessRoleOptions,
  roleOptionsLoading,
  roleOptionsError,
  onRetryRoleOptions,
  onChange,
  onClose,
  onSubmit,
  saving,
  error,
  notice,
  editConflict,
  recoveringConflict,
  onReloadLatest,
  canPublish,
}: TemplateEditorPageProps) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => { titleRef.current?.focus(); }, []);
  const updateStep = (index: number, patch: Partial<WorkflowApprovalStepDefinition>) => onChange({ ...value, approvalSteps: value.approvalSteps.map((step, current) => current === index ? { ...step, ...patch } : step) });
  const moveStep = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= value.approvalSteps.length || value.approvalSteps[target].stageOrder !== value.approvalSteps[index].stageOrder) return;
    const steps = [...value.approvalSteps];
    [steps[index], steps[target]] = [steps[target], steps[index]];
    onChange({ ...value, approvalSteps: reorderSteps(steps) });
  };
  const addStepToStage = (stageOrder: number) => onChange({
    ...value,
    approvalSteps: reorderSteps(insertWorkflowApprovalStepInStage(
      value.approvalSteps,
      stageOrder,
      newApprovalStep(value.approvalSteps.length + 1),
    )),
  });
  const mergeStageWithPrevious = (stageOrder: number) => onChange({
    ...value,
    approvalSteps: reorderSteps(mergeWorkflowApprovalStageWithPrevious(value.approvalSteps, stageOrder)),
  });
  const splitStageAt = (index: number) => onChange({
    ...value,
    approvalSteps: reorderSteps(splitWorkflowApprovalStageAt(value.approvalSteps, index)),
  });
  const approvalStageGroups = groupWorkflowApprovalSteps(value.approvalSteps);
  const moveStage = (stageOrder: number, direction: -1 | 1) => onChange({
    ...value,
    approvalSteps: reorderSteps(moveWorkflowApprovalStage(value.approvalSteps, stageOrder, direction)),
  });
  const referencedFieldKeys = useMemo(() => new Set(value.approvalSteps.flatMap((step) => {
    if (step.resolverType !== 'SUBJECT_ORG_LEADER' && step.resolverType !== 'FORM_PERSON_FIELD') return [];
    const fieldKey = String(step.resolverConfig?.fieldKey ?? '');
    return fieldKey ? [fieldKey] : [];
  })), [value.approvalSteps]);
  const changeFieldKeyReferences = (previousKey: string, nextKey: string) => onChange((current) => ({
    ...current,
    approvalSteps: current.approvalSteps.map((step) => step.resolverConfig?.fieldKey === previousKey
      ? { ...step, resolverConfig: { ...step.resolverConfig, fieldKey: nextKey } }
      : step),
  }));
  const title = editing ? editing.version.status === 'DRAFT' ? '워크플로우 초안 편집' : '새 워크플로우 버전' : '새 워크플로우 양식';
  const validationMessage = templateValidationMessage(value);
  const valid = validationMessage === '';
  return <EditPage
    title={title}
    titleRef={titleRef}
    width="wide"
    utilities={<Button startIcon={<ArrowBackOutlined />} onClick={onClose} disabled={saving}>양식 목록</Button>}
    editActions={<>
      <Typography variant="caption" color={valid ? designTokens.color.pageSupportingText : designTokens.color.pageErrorText} sx={{ flex: '1 1 260px' }}>{valid ? `${value.fields.length}개 필드 · ${approvalStageGroups.length}단계 결재선` : validationMessage}</Typography>
      <Button onClick={onClose} disabled={saving}>취소</Button>
      <Button variant="outlined" onClick={() => onSubmit(false)} disabled={saving || !valid || editConflict}>{saving ? '저장 중…' : '초안 저장'}</Button>
      {canPublish && <Button variant="contained" startIcon={<PublishOutlined />} onClick={() => onSubmit(true)} disabled={saving || !valid || editConflict}>{saving ? '게시 중…' : '저장하고 게시'}</Button>}
    </>}
  >
      <WorkflowTemplateLayout preview={<WorkflowTemplatePreview value={value} personOptions={personOptions} organizationOptions={organizationOptions} loading={personOptionsLoading} error={personOptionsError} onRetry={onRetryPersonOptions} />}>
      {error && <Alert severity={editConflict ? 'warning' : 'error'} action={editConflict ? <Button color="inherit" size="small" loading={recoveringConflict} onClick={onReloadLatest}>최신 양식 불러오기</Button> : undefined}>{error}</Alert>}
      {notice && <Alert severity="info">{notice}</Alert>}
      <SectionCard title="기본 정보"><Box sx={{ p: 1.75, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
          <FormTextField label="양식 이름" value={value.displayName} onChange={(event) => onChange({ ...value, displayName: event.target.value })} required />
          <FormTextField label="분류" value={value.category ?? ''} onChange={(event) => onChange({ ...value, category: event.target.value })} />
          <FormTextField label="설명" value={value.description ?? ''} onChange={(event) => onChange({ ...value, description: event.target.value })} sx={{ gridColumn: '1 / -1' }} />
          <WorkflowTemplateIconPicker value={value.iconKey} onChange={(iconKey) => onChange({ ...value, iconKey })} />
        </Box></SectionCard>
      <WorkflowFieldsEditor
        fields={value.fields}
        referencedFieldKeys={referencedFieldKeys}
        onChange={(fields) => onChange({ ...value, fields })}
        onFieldKeyChange={changeFieldKeyReferences}
      />
      <SectionCard title="사용 권한" help="구성원 배정 여부와 관계없이 등록된 역할을 검색합니다. 검색 결과는 최대 100개입니다." action={<Button size="small" startIcon={<AddOutlined />} onClick={() => onChange({ ...value, accessRules: [...(value.accessRules ?? []), newAccessRule()] })}>규칙 추가</Button>}>
        <Stack divider={<Divider />}>{(value.accessRules ?? []).map((rule, index) => <Box key={`${rule.capability}-${index}`} sx={{ p: 1.5, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
          <FormTextField select label="권한" value={rule.capability} onChange={(event) => updateAccessRule(value, onChange, index, { capability: event.target.value as WorkflowAccessRule['capability'] })}><MenuItem value="REQUEST"><ValueBadge label="신청" /></MenuItem><MenuItem value="READ"><ValueBadge label="열람" /></MenuItem><MenuItem value="MANAGE"><ValueBadge label="관리" /></MenuItem></FormTextField>
          <FormTextField select label="대상" value={rule.principalType} onChange={(event) => updateAccessRule(value, onChange, index, { principalType: event.target.value as WorkflowAccessRule['principalType'], principalId: undefined })}><MenuItem value="ALL_EMPLOYEES"><ValueBadge label="전체 구성원" /></MenuItem><MenuItem value="ROLE"><ValueBadge label="역할" /></MenuItem><MenuItem value="SUPERVISORY_ORGANIZATION"><ValueBadge label="조직" /></MenuItem></FormTextField>
          {rule.principalType === 'ALL_EMPLOYEES' ? <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>모든 재직 구성원</Typography>
            : rule.principalType === 'ROLE' ? <Stack spacing={1} sx={{ minWidth: 0 }}>
                <WorkflowRolePicker label="대상 역할" loadOptions={loadAccessRoleOptions} roleCode={rule.principalId ?? ''} options={roleOptions} onChange={(principalId) => updateAccessRule(value, onChange, index, { principalId })} />
              </Stack>
              : <WorkflowOrganizationPicker label="대상 조직" organizationId={rule.principalId ?? ''} options={organizationOptions} loading={personOptionsLoading} error={personOptionsError} onRetry={onRetryPersonOptions} onChange={(principalId) => updateAccessRule(value, onChange, index, { principalId })} />}
          <IconButton size="small" color="error" aria-label="사용 권한 규칙 삭제" onClick={() => onChange({ ...value, accessRules: (value.accessRules ?? []).filter((_, current) => current !== index) })}><DeleteOutlined /></IconButton>
        </Box>)}</Stack>
      </SectionCard>
      <SectionCard title="기본 결재선" action={<Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75}>
        {approvalTemplates.length > 0 ? <FormTextField select size="small" label="승인 템플릿 적용" value="" onChange={(event) => {
          const preset = approvalTemplates.find((item) => item.id === event.target.value);
          if (!preset) return;
          onChange({ ...value, approvalSteps: reorderSteps(preset.approvalSteps.map(({ id: _id, ...step }) => ({ ...step, resolverConfig: { ...(step.resolverConfig ?? {}) } }))) });
        }} sx={{ minWidth: 200 }}><MenuItem value="" disabled>템플릿 선택</MenuItem>{approvalTemplates.map((item) => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}</FormTextField> : null}
        <Button size="small" startIcon={<AddOutlined />} onClick={() => onChange({ ...value, approvalSteps: reorderSteps([...value.approvalSteps, newApprovalStep(value.approvalSteps.length + 1)]) })}>다음 단계 추가</Button>
      </Stack>}>
        <Stack spacing={1.5} sx={{ p: { xs: 1.25, sm: 1.5 } }}>{approvalStageGroups.map((stage) => <Box
          component="section"
          key={`workflow-template-stage-${stage.stageOrder}`}
          data-testid={`workflow-template-approval-stage-group-${stage.stageOrder}`}
          aria-label={`${stage.stageOrder}단계 승인자 ${stage.items.length}명`}
          sx={{ overflow: 'hidden', border: `1px solid ${designTokens.color.divider}`, borderRadius: designTokens.radius.surface, bgcolor: designTokens.color.canvas }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{ px: 1.5, py: 1.25, alignItems: { sm: 'center' }, justifyContent: 'space-between', bgcolor: designTokens.color.sidebarSurface, borderBottom: `1px solid ${designTokens.color.divider}` }}
          >
            <Stack direction="row" spacing={1} sx={{ minWidth: 0, alignItems: 'center' }}>
              <Box sx={{ width: 28, height: 28, flexShrink: 0, display: 'grid', placeItems: 'center', borderRadius: designTokens.radius.circle, bgcolor: designTokens.color.actionSoft, color: designTokens.color.actionDark, fontSize: designTokens.typography.supporting.fontSize, fontWeight: 800 }}>{stage.stageOrder}</Box>
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={0.75} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>{stage.stageOrder}단계</Typography>
                  {stage.items.length > 1 ? <ValueBadge label="병렬 승인" /> : null}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {stage.items.length > 1 ? `승인자 ${stage.items.length}명 · 모두 승인하면 다음 단계로 진행` : '승인자 1명'}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
              <IconButton size="small" aria-label={`${stage.stageOrder}단계 위로 이동`} disabled={stage.stageOrder === 1} onClick={() => moveStage(stage.stageOrder, -1)}><ArrowUpwardOutlined /></IconButton>
              <IconButton size="small" aria-label={`${stage.stageOrder}단계 아래로 이동`} disabled={stage.stageOrder === approvalStageGroups.length} onClick={() => moveStage(stage.stageOrder, 1)}><ArrowDownwardOutlined /></IconButton>
              {stage.stageOrder > 1 ? <Button size="small" variant="text" onClick={() => mergeStageWithPrevious(stage.stageOrder)}>{stage.stageOrder}단계를 이전 단계와 합치기</Button> : null}
              <Button size="small" variant="outlined" startIcon={<AddOutlined />} onClick={() => addStepToStage(stage.stageOrder)}>같은 단계 승인자 추가</Button>
            </Stack>
          </Stack>
          <Stack divider={<Divider />}>
            {stage.items.map(({ step, index }, memberIndex) => <Box key={`${step.order}-${index}`} sx={{ p: 1.5 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ alignItems: { md: 'center' } }}>
            <FormTextField label="단계명" value={step.label} onChange={(event) => updateStep(index, { label: event.target.value })} sx={{ flex: 1 }} />
            <FormTextField select label="승인 라벨" value={step.approvalLabelId ?? step.approvalLabel?.id ?? ''} onChange={(event) => {
              const selectedLabel = approvalLabels.find((label) => (label.id ?? '') === event.target.value) ?? approvalLabels[0];
              updateStep(index, { approvalLabelId: selectedLabel.id, approvalLabel: selectedLabel });
            }} sx={{ minWidth: 130 }}>{approvalLabels.map((label) => <MenuItem key={label.id ?? 'builtin'} value={label.id ?? ''}><ValueBadge label={label.name} /></MenuItem>)}</FormTextField>
            <FormTextField select label="승인자" value={step.resolverType} onChange={(event) => updateStep(index, { resolverType: event.target.value as WorkflowApprovalStepDefinition['resolverType'], resolverConfig: {} })} sx={{ minWidth: 190 }}>
              <MenuItem value="REQUESTER_ORG_LEADER"><ValueBadge label="요청자의 조직장" /></MenuItem><MenuItem value="ROOT_ORG_LEADER"><ValueBadge label="공식 최상위 조직장" /></MenuItem><MenuItem value="FIXED_PERSON"><ValueBadge label="특정 구성원" /></MenuItem><MenuItem value="FIXED_ROLE"><ValueBadge label="특정 역할" /></MenuItem><MenuItem value="FORM_PERSON_FIELD"><ValueBadge label="양식의 구성원 필드" /></MenuItem><MenuItem value="SUBJECT_ORG_LEADER"><ValueBadge label="대상 구성원의 조직장" /></MenuItem>
            </FormTextField>
            {(step.resolverType === 'SUBJECT_ORG_LEADER' || step.resolverType === 'FORM_PERSON_FIELD') && <FormTextField select label="구성원 필드" value={String(step.resolverConfig?.fieldKey ?? '')} onChange={(event) => updateStep(index, { resolverConfig: { ...step.resolverConfig, fieldKey: event.target.value } })} sx={{ minWidth: 150 }}>{value.fields.filter((field) => field.type === 'PERSON').map((field) => <MenuItem key={field.key} value={field.key}>{field.label}</MenuItem>)}</FormTextField>}
            {step.resolverType === 'REQUESTER_ORG_LEADER' || step.resolverType === 'SUBJECT_ORG_LEADER' ? <FormTextField type="number" label="조직 단계" value={Number(step.resolverConfig?.level ?? 1)} onChange={(event) => updateStep(index, { resolverConfig: { ...step.resolverConfig, level: Math.max(1, Number(event.target.value)) } })} sx={{ width: 110 }} /> : null}
            {step.resolverType === 'FIXED_PERSON' && <Box sx={{ minWidth: { md: 260 }, flex: '1 1 280px' }}><WorkflowPersonPicker
              label="구성원"
              personId={String(step.resolverConfig?.personId ?? '')}
              options={personOptions}
              loading={personOptionsLoading}
              error={personOptionsError}
              onRetry={onRetryPersonOptions}
              onChange={(personId) => updateStep(index, { resolverConfig: { personId } })}
            /></Box>}
            {step.resolverType === 'FIXED_ROLE' && <Box sx={{ minWidth: { md: 240 }, flex: '1 1 260px' }}><WorkflowRolePicker
              loadOptions={loadRoleOptions}
              label="승인 역할"
              roleCode={String(step.resolverConfig?.roleCode ?? '')}
              options={roleOptions}
              loading={roleOptionsLoading}
              error={roleOptionsError}
              onRetry={onRetryRoleOptions}
              onChange={(roleCode) => updateStep(index, { resolverConfig: { roleCode } })}
            /></Box>}
            {memberIndex > 0 ? <Button size="small" onClick={() => splitStageAt(index)}>이 승인자부터 다음 단계로 분리</Button> : null}
            <IconButton size="small" aria-label={`${step.label} 같은 단계에서 위로 이동`} disabled={memberIndex === 0} onClick={() => moveStep(index, -1)}><ArrowUpwardOutlined /></IconButton>
            <IconButton size="small" aria-label={`${step.label} 같은 단계에서 아래로 이동`} disabled={memberIndex === stage.items.length - 1} onClick={() => moveStep(index, 1)}><ArrowDownwardOutlined /></IconButton>
            <IconButton size="small" color="error" aria-label={`${step.label} 삭제`} onClick={() => onChange({ ...value, approvalSteps: reorderSteps(value.approvalSteps.filter((_, current) => current !== index)) })}><DeleteOutlined /></IconButton>
          </Stack>
          <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreOutlined />}><Typography variant="body2">{step.label || `${step.order}번째 승인자`} 세부 설정</Typography></AccordionSummary>
            <AccordionDetails><Stack spacing={1.5}>
            <ChoiceControl kind="checkbox" size="small" checked={step.requesterEditable} onCheckedChange={(checked) => updateStep(index, { requesterEditable: checked })} label="요청자가 수정 가능" />
            <FormTextField select label="승인자를 못 찾으면" value={step.missingPolicy} onChange={(event) => updateStep(index, { missingPolicy: event.target.value as WorkflowApprovalStepDefinition['missingPolicy'] })} sx={{ minWidth: 210 }}><MenuItem value="FAIL"><ValueBadge label="제출 차단" /></MenuItem><MenuItem value="SKIP"><ValueBadge label="단계 생략" /></MenuItem><MenuItem value="FALLBACK_ROOT_ORG_LEADER"><ValueBadge label="CEO로 대체" /></MenuItem></FormTextField>
            <FormTextField select label="본인이 승인자면" value={step.selfPolicy} onChange={(event) => updateStep(index, { selfPolicy: event.target.value as 'FAIL' | 'SKIP' })} sx={{ minWidth: 170 }}><MenuItem value="SKIP"><ValueBadge label="단계 생략" /></MenuItem><MenuItem value="FAIL"><ValueBadge label="제출 차단" /></MenuItem></FormTextField>
            <FormTextField select label="앞 단계와 같으면" value={step.duplicatePolicy} onChange={(event) => updateStep(index, { duplicatePolicy: event.target.value as 'KEEP' | 'SKIP' })} sx={{ minWidth: 180 }}><MenuItem value="SKIP"><ValueBadge label="중복 생략" /></MenuItem><MenuItem value="KEEP"><ValueBadge label="그대로 유지" /></MenuItem></FormTextField>
            <WorkflowDurationField label="처리 기한" value={step.dueAfterMinutes === undefined ? '' : String(step.dueAfterMinutes)} onChange={(minutes) => updateStep(index, { dueAfterMinutes: minutes.trim() ? Number(minutes) : undefined })} helperText="비워두면 별도 기한을 정하지 않습니다." />
          </Stack></AccordionDetails></Accordion>
        </Box>)}
          </Stack>
        </Box>)}</Stack>
      </SectionCard>
      </WorkflowTemplateLayout>
  </EditPage>;
}
